/**
 * The exit word rules — D227 (E21, Calder's `the-road-climbs` §1): what an
 * exit's words — its `directionToken` (D230) and its `alsoAnswersTo` — must
 * name before a player can ever move by it.
 *
 * @remarks
 * An exit answers to its `directionToken` and to every word it also answers
 * to; movement looks the typed direction up across the room's exits and takes
 * the first that answers. Two things make such a word dead or dishonest, and
 * both are silent without this check — the pack loads, the word sits in the
 * JSON, and the player either never reaches it or reaches the wrong door:
 *
 * - The word is **no direction** in the merged vocabulary (a typo, a word the
 *   pack never declared), or an **alias** of one — the engine looks exits up
 *   by the canonical token the parser emits, so `ascend` on an exit would
 *   answer nobody while `up` answers `ascend` too. D227 judged only the
 *   extra words this way and left the `directionToken` to the schema's
 *   "non-blank string" (its v0 note deferred the cross-check); D230 judges
 *   it by the same rule, because it is the same rule, and a dead
 *   `directionToken` is the deader of the two: the map draws the way, and
 *   the player cannot walk it.
 * - **Two exits in one room answer the same word** — one exit's extra word
 *   against another's `directionToken`, or against another's extra word. The
 *   first in authored order would win every time, and the second would be a
 *   way out that is not there. The room's own `directionToken`s are judged by
 *   the same rule, because it is the same rule.
 *
 * Judged against the MERGED, compiled vocabulary like the verb rules
 * (`authored-verb-rules.ts`), because every pack inherits `up` and `in` from
 * the base it extends.
 *
 * @packageDocumentation
 */

import type { ContentPackDefinition } from '@orpheus/content-schema';
import type { CompiledLexicon } from '@orpheus/parser-engine';

import { brokenReference } from './broken-reference.js';
import { type ReferenceBrokenFailure } from './load-failure.js';

/**
 * D227: every word an exit answers to must be a direction nothing else in the
 * room answers to.
 *
 * @param contentPack - The parsed pack
 * @param lexicon - The merged, compiled vocabulary (the authority on which directions exist)
 * @returns One failure per dead or contested word, all collected
 */
export const validateExitWords = (contentPack: ContentPackDefinition, lexicon: CompiledLexicon): readonly ReferenceBrokenFailure[] => {
	const failures: ReferenceBrokenFailure[] = [];

	contentPack.rooms.forEach((room, roomIndex) => {
		/** Each word some exit of this room already answers to → the path that claimed it. */
		const claimedBy = new Map<string, string>();

		room.exits.forEach((exit, exitIndex) => {
			const exitPath = `rooms[${String(roomIndex)}].exits[${String(exitIndex)}]`;

			// D230 (D227's owed cross-check): the exit's OWN word is judged
			// first, by the same rule as its other words — until now a
			// `directionToken` no vocabulary declared loaded silently, and the
			// exit it named could never be taken.
			for (const [wordIndex, word] of [exit.directionToken, ...(exit.alsoAnswersTo ?? [])].entries()) {
				const wordPath = wordIndex === 0 ? `${exitPath}.directionToken` : `${exitPath}.alsoAnswersTo[${String(wordIndex - 1)}]`;
				const direction = lexicon.directionsByToken.get(word);

				if (direction === undefined) {
					failures.push(brokenReference(wordPath, word, 'no direction by that word is declared, so nothing could ever move by it'));
				} else if ((direction as string) !== word) {
					failures.push(
						brokenReference(wordPath, word, `"${word}" is an alias of "${direction as string}" — name the canonical direction, which answers every alias`),
					);
				}

				const earlierClaim = claimedBy.get(word);

				if (earlierClaim === undefined) {
					claimedBy.set(word, wordPath);
				} else {
					failures.push(
						brokenReference(wordPath, word, `two exits of room "${room.roomId}" answer to "${word}" (${earlierClaim} already does) — the second could never be taken`),
					);
				}
			}
		});
	});

	return failures;
};
